import React, { useEffect, useState } from "react";
import { usePageMeta } from "@/lib/seo";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { CheckCircle2, TrendingDown, Clock, MapPin } from "lucide-react";
import api from "@/lib/api";

const CATEGORY_LABEL = {
  "post-asta": "Debito residuo post-asta",
  "pre-asta": "Salvataggio pre-asta",
  "pmi": "PMI / Aziendale",
  "cartelle": "Cartelle esattoriali",
  "mutuo": "Mutuo residuo",
  "personale": "Debiti personali",
};

export default function Cases() {
  usePageMeta(
    "Casi di Successo — Debiti ridotti fino al 96% | Sdebitiamoci Srl",
    "Casi reali risolti: mutuo residuo di 57.000€ chiuso a 2.500€, aste fermate, aziende salvate. Scopri i risultati del saldo e stralcio."
  );
  const [items, setItems] = useState([]);
  const [studies, setStudies] = useState([]);
  useEffect(() => {
    api.get("/testimonials").then(r => setItems(r.data));
    api.get("/case-studies").then(r => setStudies(r.data)).catch(()=>{});
  }, []);

  return (
    <div className="pt-32 pb-24" data-testid="cases-page">
      {/* ------------------------------------------------------
          HERO
          ------------------------------------------------------ */}
      <section className="container-editorial">
        <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-[#2d6fc4]">— Casi di successo</p>
        <h1 className="font-serif text-5xl md:text-[5.5rem] leading-[0.88] mt-6 text-[#2d6fc4]">
          Numeri che <em className="italic text-[#2d6fc4]">valgono case</em>.
        </h1>
        <p className="mt-8 max-w-2xl text-lg text-[#333333]">
          Alcuni dei risultati raggiunti negli ultimi anni. Ogni caso è una storia diversa — nessun copia-incolla, nessuna promessa standard.
        </p>
      </section>

      {/* ------------------------------------------------------
          CASI RISOLTI (structured case studies)
          ------------------------------------------------------ */}
      <section className="mt-24 bg-[#F1F5F9] py-24 border-y border-[#E5E7EB]" data-testid="case-studies-section">
        <div className="container-editorial">
          <div className="flex items-end justify-between mb-14">
            <div>
              <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-[#2d6fc4]">— Casi risolti</p>
              <h2 className="font-serif text-5xl md:text-6xl mt-4 leading-[0.95] text-[#2d6fc4]">
                Anche dopo l'asta,<br/><em className="italic text-[#2d6fc4]">si può ripartire.</em>
              </h2>
              <p className="mt-6 max-w-2xl text-[#333333]">
                Ricostruzione fedele di tre situazioni tra le più critiche che abbiamo chiuso. Nomi omessi per riservatezza.
              </p>
            </div>
          </div>

          <div className="space-y-8">
            {studies.map((s, i) => (
              <motion.article
                key={s.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7, delay: i*0.05 }}
                className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden"
                data-testid={`case-study-${i}`}
              >
                {/* Top strip */}
                <div className="flex flex-wrap items-center justify-between gap-4 px-8 py-5 bg-[#1a4a8a] text-white">
                  <div className="flex items-center gap-5 flex-wrap">
                    <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-white">
                      Caso #{String(i+1).padStart(2,"0")} · {CATEGORY_LABEL[s.category] || s.category}
                    </span>
                    <span className="inline-flex items-center gap-1.5 text-xs text-white">
                      <MapPin size={12}/> {s.location}
                    </span>
                    <span className="inline-flex items-center gap-1.5 text-xs text-white">
                      <Clock size={12}/> {s.duration}
                    </span>
                  </div>
                  <span className="font-serif text-4xl text-white" data-testid={`case-study-saving-${i}`}>
                    {s.saving_pct === 100 ? "Azzerato" : `-${s.saving_pct}%`}
                  </span>
                </div>

                <div className="grid grid-cols-12 gap-0">
                  {/* Left: title + before/after */}
                  <div className="col-span-12 md:col-span-4 p-8 border-b md:border-b-0 md:border-r border-[#E5E7EB] bg-[#F8FAFC]">
                    <h3 className="font-serif text-3xl leading-tight text-[#2d6fc4]">{s.title}</h3>
                    <div className="mt-8 space-y-4 text-sm">
                      <div>
                        <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#333333]">Debito iniziale</p>
                        <p className="font-serif text-3xl text-[#2d6fc4] mt-1">{s.debt_before}</p>
                      </div>
                      <div className="flex items-center gap-2 text-[#2d6fc4]">
                        <TrendingDown size={14}/> <span className="font-mono text-[10px] tracking-[0.2em] uppercase">Saldo</span>
                      </div>
                      <div>
                        <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#333333]">Chiuso con</p>
                        <p className="font-serif text-3xl text-[#2d6fc4] mt-1">{s.debt_after}</p>
                      </div>
                    </div>
                  </div>

                  {/* Right: narrative in 3 chapters */}
                  <div className="col-span-12 md:col-span-8 p-8 md:p-10 space-y-8">
                    <NarrativeBlock n="01" label="La situazione" body={s.situation}/>
                    <NarrativeBlock n="02" label="Il nostro intervento" body={s.intervention}/>
                    <NarrativeBlock n="03" label="Il risultato" body={s.result} highlight/>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>

          <div className="mt-14 flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white border border-[#E5E7EB] rounded-lg p-8">
            <p className="font-serif text-2xl md:text-3xl text-[#2d6fc4] max-w-2xl leading-tight">
              Il tuo caso è diverso — ma il metodo è lo stesso. <span className="italic text-[#2d6fc4]">Contattaci per una consulenza conoscitiva.</span>
            </p>
            <Link to="/contatti" data-testid="cases-cta" className="btn-cta inline-flex justify-center px-7 py-4 rounded-lg text-sm">
              Consulenza gratuita
            </Link>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------
          TESTIMONIALS grid
          ------------------------------------------------------ */}
      <section className="container-editorial mt-24">
        <div className="mb-10">
          <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-[#2d6fc4]">— Voci dei clienti</p>
          <h2 className="font-serif text-5xl md:text-6xl mt-4 leading-[0.95] text-[#2d6fc4]">
            Testimonianze <em className="italic text-[#2d6fc4]">dei nostri clienti</em>.
          </h2>
        </div>

        <div className="grid grid-cols-12 gap-6">
          {items.map((c, i) => (
            <motion.article
              key={c.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: (i%3)*0.08 }}
              className={`case-card border border-[#E5E7EB] bg-[#FFFFFF] rounded-lg p-8 flex flex-col ${i%3===0 ? "col-span-12 md:col-span-8" : "col-span-12 md:col-span-4"}`}
              data-testid={`case-card-${i}`}
            >
              <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#2d6fc4]">{c.role}</p>
              <p className={`mt-6 font-serif text-[#2d6fc4] leading-tight ${i%3===0 ? "text-4xl md:text-5xl" : "text-3xl"}`}>
                “{c.quote}”
              </p>
              <div className="mt-auto pt-8 border-t border-[#E5E7EB] flex items-end justify-between">
                <div>
                  <p className="font-medium">{c.name}</p>
                  <p className="text-xs text-[#333333] mt-1">{c.debt_before} → {c.debt_after}</p>
                </div>
                <p className="font-serif text-5xl text-[#2d6fc4]">-{c.saving_pct}%</p>
              </div>
            </motion.article>
          ))}
        </div>
      </section>

      <section className="container-editorial mt-24 text-center">
        <p className="font-serif text-3xl md:text-4xl text-[#333333] max-w-3xl mx-auto leading-tight">
          Ogni cifra sopra è un mutuo pagato, un pignoramento fermato, una notte serena. <span className="text-[#2d6fc4]">Il tuo turno.</span>
        </p>
        <Link to="/contatti" className="mt-8 inline-flex btn-cta px-7 py-4 rounded-lg text-sm">
          Consulenza gratuita
        </Link>
      </section>
    </div>
  );
}

function NarrativeBlock({ n, label, body, highlight }) {
  return (
    <div className={highlight ? "border-l-2 border-[#14417B] pl-6" : "pl-6 border-l-2 border-[#E5E7EB]"}>
      <div className="flex items-center gap-3">
        <span className="chapter-num text-[#2d6fc4] text-3xl">{n}</span>
        <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#333333]">{label}</p>
        {highlight && <CheckCircle2 size={14} className="text-[#2d6fc4]" strokeWidth={2}/>}
      </div>
      <p className={`mt-3 text-[#333333] leading-relaxed ${highlight ? "text-lg font-medium" : ""}`}>{body}</p>
    </div>
  );
}
